import { cva, type VariantProps } from 'class-variance-authority'
import { createContext, useContext } from 'react'
import {
	Tab as AriaTab,
	TabList as AriaTabList,
	type TabListProps as AriaTabListProps,
	TabPanel as AriaTabPanel,
	type TabPanelProps as AriaTabPanelProps,
	type TabProps as AriaTabProps,
	Tabs as AriaTabs,
	type TabsProps as AriaTabsProps,
} from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link TabList}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - `default`: segmented control strip — a filled surface the active tab sits on as a pill
 * - `underline`: page-navigation strip — tabs separated by spacing, active marked by a rule
 */
export const tabListVariants = cva('inline-flex items-center', {
	variants: {
		variant: {
			default: 'h-10 justify-center rounded-ds-md bg-ds-surface p-1 text-ds-text-muted',
			underline: 'gap-6 text-ds-text-muted',
		},
	},
	defaultVariants: { variant: 'default' },
})

/**
 * Variant styles for {@link Tab}.
 *
 * @remarks
 * - Active state styles via `data-[selected]` (React Aria sets the attribute only while selected)
 * - Disabled state styles via `data-[disabled]` — the tab renders a `<div>`, so `disabled:` never matches
 */
export const tabVariants = cva(
	'inline-flex cursor-pointer items-center justify-center whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring focus-visible:ring-offset-2 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
	{
		variants: {
			variant: {
				default:
					'rounded-ds-sm px-3 py-1.5 text-ds-sm font-medium data-[selected]:bg-ds-background data-[selected]:text-ds-text data-[selected]:shadow-ds-sm',
				// The active tab is marked by weight and its own rule, not by a filled
				// pill, so the strip reads as page navigation rather than a control.
				underline:
					'border-b-2 border-transparent pb-2 text-ds-sm font-medium hover:text-ds-text data-[selected]:border-ds-text data-[selected]:font-semibold data-[selected]:text-ds-text',
			},
		},
		defaultVariants: { variant: 'default' },
	},
)

type TabsVariant = VariantProps<typeof tabListVariants>['variant']

/** Shared so a list and its tabs cannot drift onto different variants. */
const TabsVariantContext = createContext<TabsVariant>('default')

export interface TabsProps extends Omit<AriaTabsProps, 'className'> {
	className?: string
}

/**
 * Accessible tabs primitive built on React Aria Components.
 *
 * @remarks
 * - **Selection:** `defaultSelectedKey` (uncontrolled) or `selectedKey` + `onSelectionChange`;
 *   `Tab` and `TabPanel` pair through matching `id`s.
 * - **Panels:** an unselected `TabPanel` is not mounted, so a tab costs nothing until selected.
 * - **Keyboard:** arrow-key navigation with automatic activation (`keyboardActivation="manual"` to opt out).
 *
 * @example
 * ```tsx
 * <Tabs defaultSelectedKey="overview">
 *   <TabList>
 *     <Tab id="overview">Overview</Tab>
 *     <Tab id="details">Details</Tab>
 *   </TabList>
 *   <TabPanel id="overview">…</TabPanel>
 *   <TabPanel id="details">…</TabPanel>
 * </Tabs>
 * ```
 */
export function Tabs({ className, ...props }: TabsProps) {
	return <AriaTabs {...props} className={className} />
}

export interface TabListProps<T extends object> extends Omit<AriaTabListProps<T>, 'className'> {
	/** Visual style shared with every child Tab: 'default' (segmented) | 'underline'. */
	variant?: TabsVariant
	className?: string
}

/**
 * The tab strip. Publishes its `variant` to child {@link Tab}s via context.
 */
export function TabList<T extends object>({ variant = 'default', className, ...props }: TabListProps<T>) {
	return (
		<TabsVariantContext.Provider value={variant}>
			<AriaTabList {...props} className={cn(tabListVariants({ variant }), className)} />
		</TabsVariantContext.Provider>
	)
}

export interface TabProps extends Omit<AriaTabProps, 'className'> {
	/** Overrides the variant inherited from the enclosing TabList. */
	variant?: TabsVariant
	className?: string
}

/**
 * One tab in the strip. Renders `role="tab"` with `aria-selected`; content is
 * exactly its children — no injected icons or text, so accessible names equal
 * what the call site wrote.
 */
export function Tab({ variant, className, ...props }: TabProps) {
	const inherited = useContext(TabsVariantContext)
	return <AriaTab {...props} className={cn(tabVariants({ variant: variant ?? inherited }), className)} />
}

export interface TabPanelProps extends Omit<AriaTabPanelProps, 'className'> {
	className?: string
}

/**
 * A tab's panel. Not mounted while unselected; focusable only when it contains
 * no tabbable child.
 */
export function TabPanel({ className, ...props }: TabPanelProps) {
	return (
		<AriaTabPanel
			{...props}
			className={cn(
				'mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring focus-visible:ring-offset-2',
				className,
			)}
		/>
	)
}
